import React, { useState, useEffect } from 'react';
import './Button.css';

const Button = React.forwardRef(function Button({
  children,
  icon: Icon,
  variant = 'primary',
  size = 'default', // 'small', 'default', 'large'
  type = 'default', // 'default', 'fixed', 'inversed', 'record', 'icon-only', 'picker'
  htmlType = 'button', // native button type: 'button' | 'submit' | 'reset'
  
  // Props pour le comportement responsive
  collapseOnSmallScreen = false,
  lowerThreshold = 500,
  upperThreshold = 500,
  
  // Props pour l'icône
  iconSize = 'default',
  isSpinning = false,
  
  // Props spéciaux
  highlight = false,
  isRecording = false,
  isSelected = false, // Pour le picker
  
  ...props
}, ref) {
  // Gestion du responsive (seulement si collapseOnSmallScreen est true et type n'est pas 'fixed')
  const [isSmallScreen, setIsSmallScreen] = useState(
    window.innerWidth < lowerThreshold
  );

  useEffect(() => {
    if (!collapseOnSmallScreen || type === 'fixed') return;

    function handleResize() {
      const width = window.innerWidth;
      setIsSmallScreen((prev) => {
        if (!prev && width < lowerThreshold) return true;
        if (prev && width > upperThreshold) return false;
        return prev;
      });
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [lowerThreshold, upperThreshold, collapseOnSmallScreen, type]);

  // Déterminer si on cache le texte
  const hideText = collapseOnSmallScreen && isSmallScreen && type !== 'fixed';

  // Construction des classes CSS
  const getButtonClasses = () => {
    const classes = ['button'];
    
    // Type de bouton
    classes.push(`button-${type}`);
    
    // Variante de couleur
    classes.push(`button-${variant}`);
    
    // Taille
    classes.push(`button-size-${size}`);
    
    // États spéciaux
    if (highlight) classes.push('highlight');
    if (isRecording && type === 'record') classes.push('recording');
    if (hideText && type === 'default') classes.push('collapsed');
    if (isSelected && type === 'picker') classes.push('selected');
    
    return classes.join(' ');
  };

  // Classes pour l'icône
  const getIconClasses = () => {
    const classes = ['button-icon'];
    
    if (iconSize !== 'default') classes.push(`icon-${iconSize}`);
    if (isSpinning) classes.push('spinning');
    if (hideText && type === 'default') classes.push('icon-only');
    
    return classes.join(' ');
  };

  // Classes pour le texte
  const getTextClasses = () => {
    const classes = ['button-text'];
    
    if (hideText) classes.push('hidden');
    if (type === 'inversed') classes.push('text-inversed');
    
    return classes.join(' ');
  };

  // Rendu pour le type 'record' (toujours circulaire, sans texte)
  if (type === 'record') {
    return (
      <button className={getButtonClasses()} type={htmlType} ref={ref} {...props}>
        {Icon && <Icon className={getIconClasses()} />}
      </button>
    );
  }

  // Rendu pour le type 'icon-only' (juste une icône, pas de texte)
  if (type === 'icon-only') {
    return (
      <button className={getButtonClasses()} type={htmlType} ref={ref} {...props}>
        {Icon && <Icon className={getIconClasses()} />}
      </button>
    );
  }

  // Rendu pour le type 'picker' (sélecteur avec état)
  if (type === 'picker') {
    return (
      <button className={getButtonClasses()} type={htmlType} ref={ref} {...props}>
        {Icon && <Icon className={getIconClasses()} />}
        {children && (
          <span className={getTextClasses()}>
            {children}
          </span>
        )}
        {/* Indicateur de sélection */}
        <span className="picker-indicator" />
      </button>
    );
  }

  // Rendu pour tous les autres types
  return (
    <button className={getButtonClasses()} type={htmlType} ref={ref} {...props}>
      {Icon && <Icon className={getIconClasses()} />}
      {children && (
        <span className={getTextClasses()}>
          {children}
        </span>
      )}
    </button>
  );
});

export default Button;